import { useRef, useEffect, useState } from "react";

import LoadingSkeleton from "components/atoms/LoadingSkeleton/LoadingSkeleton";
import usePermissions from "hooks/usePermissions";
import { isEqual } from "helpers/commonHelpers";
import useStatusWidgetData from "api/queries/user/useGetStatusWidgetData";
import { distributeItemsIntoRows, IStatusItem } from "./StatusWidgetUtils";
import StatusItem from "./components/StatusItem";
import "./StatusWidget.scss";

type GridStyles = {
  rows: IStatusItem[][];
  maxColumns: number;
};

function StatusWidget() {
  const containerRef = useRef(null);
  const { permissions } = usePermissions();
  const { user_type, permissions: userPermissions } = JSON.parse(permissions || "{}");

  const [isCompact, setIsCompact] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const [gridStyles, setGridStyles] = useState<GridStyles>({
    rows: [],
    maxColumns: 0,
  });

  const { data: statusData, isFetching } = useStatusWidgetData(user_type, userPermissions, {
    staleTime: 300000,
  });

  useEffect(() => {
    if (!containerRef.current || !gridStyles.rows) return;

    const resizeObserver = new ResizeObserver(([entry]) => {
      const { width } = entry.contentRect;

      const compactBreakpoint = 1100;
      const mobileBreakpoint = 600;

      setIsMobile(width < mobileBreakpoint);
      setIsCompact(width >= mobileBreakpoint && width < compactBreakpoint);
    });

    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!statusData || statusData.length === 0) return;

    // Only proceed if statusData has actually changed
    let maxColumns = statusData.length;
    if (isCompact) maxColumns = Math.ceil(statusData.length / 2);
    if (isMobile) maxColumns = 3;

    const rows = distributeItemsIntoRows(statusData, maxColumns);

    // Only update gridStyles if it actually changes
    const newGridStyles = { rows, maxColumns };
    setGridStyles((prevGridStyles) => {
      if (isEqual(prevGridStyles, newGridStyles)) {
        return prevGridStyles;
      }
      return newGridStyles;
    });
  }, [statusData, isCompact, isMobile]);

  return (
    <div ref={containerRef} className="status-widget-container">
      {isFetching ? (
        <LoadingSkeleton rows={1} />
      ) : (
        <>
          {gridStyles.rows.map((row, rowIndex) => {
            const columns = row.length;
            return (
              <div
                style={{
                  display: "grid",
                  gridTemplateColumns: `repeat(${columns}, 1fr)`,
                  marginBottom: `${(isMobile || isCompact) && rowIndex !== gridStyles.rows.length - 1 ? "20px" : 0}`,
                }}
              >
                {row.map((item) => {
                  const lastItem = row[row.length - 1] === item;

                  return (
                    <StatusItem
                      displayTitle={item.displayTitle}
                      title={item.group_name}
                      count={item.count}
                      status={item.status}
                      lastItem={item.lastInGroup && !lastItem}
                    />
                  );
                })}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

export default StatusWidget;
